import * as React from 'react'
// libs
import { useSelector } from 'react-redux'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// constants
import { ENTITIES } from 'constants/index'
// hooks
import { useCurrentUser } from 'hooks/index'
// store
import { objectSecuritiesSelector } from 'store/reducers/objectSecurities'
import { securityProfilesSelector } from 'store/reducers/securityProfiles'
// types
import {
  IPlan,
  IPlanKpi,
  IPlanType,
  IPlanAction,
  CanViewType,
  IPlanObjective,
  IObjectSecurity,
  ISecurityProfile,
  IFieldValueMapItem,
  ISecurityProfilePermissions, IPlanItem
} from 'types/graphql'
// utils
import { omit, isEmpty } from 'lodash'
import { isCurrentUser } from 'utils/user'
import { mergePermissions } from 'utils/permissions'
import { getPlanFromEntity } from 'utils/tables'
import { get, map, pipe, find, filter, flattenDeep } from 'lodash/fp'

type Entity = (IPlan | IPlanAction | IPlanKpi | IPlanObjective) & { parentPermissions?: ISecurityProfilePermissions[] }

type ExtendedPlanItem = IPlanItem & { entity: Entity, entityType: keyof typeof ENTITIES, children: ExtendedPlanItem[] }

export const usePermissions = ({
  isKPI,
  isPlan,
  entity,
  isAction,
  isObjective
}: {
  isKPI?: boolean
  entity: Entity
  isPlan?: boolean
  isAction?: boolean
  isObjective?: boolean
}) => {
  const user = useCurrentUser()

  const objectSecurities = useSelector(objectSecuritiesSelector)
  const securityProfiles = useSelector(securityProfilesSelector)

  const filteredGroups = user.securityGroups.filter(
    securityGroup => securityGroup.active && securityGroup.businessRules[0].views[0].canView === CanViewType.Plan
  )

  const groupSecurityPermissions: ISecurityProfilePermissions = pipe(
    filter(({ id }) =>
      filteredGroups.some(group =>
        group.businessRules[0].users.some(({ securityProfileId }) => securityProfileId === id)
      )
    ),
    map<ISecurityProfile, ISecurityProfilePermissions>(({ permissions }) => permissions),
    mergePermissions
  )(securityProfiles)

  const { plan: changeablePlan } = React.useContext(PlanEditPageContext)

  const plan = changeablePlan || (isPlan ? (entity as IPlan) : getPlanFromEntity(entity))
  const defaultSecurities = plan && omit(plan.planType, ['description', 'fields', 'id', 'levels'])

  // methods
  const getPermissionsById = (ids: string | string[]): ISecurityProfilePermissions[] => {
    const idsArray = Array.isArray(ids) ? ids : [ids]
    return pipe(
      filter<IObjectSecurity>(({ id }) => idsArray.includes(id)),
      map(({ securityProfiles }) => securityProfiles),
      flattenDeep,
      filter(({ active }) => active),
      map(({ permissions }) => permissions)
    )(objectSecurities)
  }

  const getOwnerPermissions = ({
    isKPI,
    isPlan,
    isAction,
    isObjective,
    defaultSecurities
  }: {
    isKPI?: boolean
    isPlan?: boolean
    isAction?: boolean
    isObjective?: boolean
    defaultSecurities: Partial<IPlanType>
  }) => {
    if (isAction) {
      return getPermissionsById(defaultSecurities.actionOwnerObjectSecurityId)
    }
    if (isKPI) {
      return getPermissionsById(defaultSecurities.kpiOwnerObjectSecurityId)
    }
    if (isObjective) {
      return getPermissionsById(defaultSecurities.objectiveOwnerObjectSecurityId)
    }
    if (isPlan) {
      return getPermissionsById(defaultSecurities.planOwnerObjectSecurityId)
    }
  }

  const getMembersPermissions = (entity: Entity) => {
    return pipe(
      find((item: IFieldValueMapItem) => item.fieldName === 'membersUserPosition'),
      get('fieldValue'),
      filter((item: { value: string; objectSecurityId: string }) => isCurrentUser(item.value, user)),
      map(({ objectSecurityId }) => objectSecurityId),
      getPermissionsById
    )(entity.fieldValueMap)
  }

  const isUserOwner = (entity: Entity): boolean => isCurrentUser(entity.ownedById, user)

  const setParentPermissions = (tree: ExtendedPlanItem[], parentPermissions: ISecurityProfilePermissions[] = []) => {
    return tree.map((planItem) => {
      const isKPI = planItem.entityType === ENTITIES.PLAN_KPI
      const isAction = planItem.entityType === ENTITIES.PLAN_ACTION
      const isObjective = planItem.entityType === ENTITIES.PLAN_OBJECTIVE

      const isOwner = isUserOwner(planItem.entity)
      const membersPermissions = getMembersPermissions(planItem.entity)

      const entityPermissions = [
        ...(membersPermissions ? membersPermissions : []),
        ...(isOwner ? getOwnerPermissions({ isKPI, isAction, isObjective, defaultSecurities }) : []),
        ...parentPermissions
      ]

      const entity = {
        ...planItem.entity,
        entityPermissions,
        parentPermissions: planItem.parentId ? parentPermissions : []
      }

      return {
        ...planItem,
        entity,
        children: planItem.children ? setParentPermissions(planItem.children, entityPermissions) : []
      }
    })
  }


  const permissions: ISecurityProfilePermissions[] = [
    ...(groupSecurityPermissions ? [groupSecurityPermissions] : []),
    ...(getMembersPermissions(entity) || []),
    ...(getMembersPermissions(plan) || []),
    ...((!isPlan && isUserOwner(entity)) && getOwnerPermissions({ defaultSecurities, isAction, isKPI, isObjective }) || []),
    ...(isUserOwner(plan) && getOwnerPermissions({ defaultSecurities, isPlan: true }) || []),
    ...(entity.parentPermissions || [])
  ]

  if (!isEmpty(permissions)) {
    return { ...mergePermissions(permissions), setParentPermissions }
  } else {
    return { setParentPermissions }
  }
}
